import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import Helmet from 'react-helmet';
import {ThemeProvider, injectGlobal} from 'styled-components';
import {GlobalStyled} from '../../styles/components/global.style';
import {changeLanguage} from '../../actions/settings.action';
import {addVoucher, deleteBasket, getBasket, reorder} from '../../actions/basket.action';
import 'url-search-params-polyfill';

import {withRouter} from 'react-router-dom';

import {addLocaleData, IntlProvider} from 'react-intl';
import en from 'react-intl/locale-data/en';
import zh from 'react-intl/locale-data/zh';
import translations from '../../locale/index';
import {templates} from '../../styles/themes';

import {FOODIT_LOCALSTORAGE, ORDER_SOURCE, STATUSES, URL_QUERY_PARAMS} from '../../constants/constant';
import {BASKET_UUID_REGEX, CD_ORDER_SOURCE_REGEX} from '../../constants/regex';

import seoHelper from '../../helpers/seo.helper';
import {setCheckoutData} from '../../actions/checkoutData.action';
import {getValueFromLocalStorage, persistValueToLocalStorage, removeValueFromLocalStorage} from '../../helpers/localStorage.helper';
import {showServerErrorAlert} from '../../actions/error.action';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {importIntlPolyfill} from '../../helpers/polyfill.helper';
import {setOrderSource} from '../../actions/orderSource.action';
import UnderMaintenance from '../UnderMaintenance/UnderMaintenance';
import {ThemeAllocator} from '../../components/ThemeAllocator/ThemeAllocator';
import {getMobileOperatingSystem} from '../../helpers/mobile.helper';
import {getSiteUrls} from '../../helpers/url.helper';

addLocaleData([...en, ...zh]);

export class App extends PureComponent {
  constructor(props) {
    super(props);
    this.navUrls = getSiteUrls();
  }
  componentWillMount() {
    injectGlobal`${GlobalStyled()}`; // eslint-disable-line no-unused-expressions
    importIntlPolyfill();

    const {getBasketFunc, basket, deleteBasketFunc, setCheckoutDataFunc, setServerErrorFunc, history, location, reorderFunc, addVoucherFunc} = this.props;
    const restaurant = this.context.providers.restaurant.getMeta();
    const params = new URLSearchParams(location.search);
    const storedBasketUuid = getValueFromLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID);
    const path = location.pathname;
    this.mobileOS = getMobileOperatingSystem();

    if ((new RegExp(CD_ORDER_SOURCE_REGEX)).test(location.search)) {
      this.props.setOrderSourceFunc(ORDER_SOURCE.CENTRAL_DISH);
    }

    if (path.indexOf('/order') === 0 && path.indexOf('confirmation') === -1) { // Checkout page case, basketUuid priority from url
      const basketUuidFromUrlParams = path.match(BASKET_UUID_REGEX);

      if (!basketUuidFromUrlParams && !__SERVER__) { // If no basketUuid is extracted from the url, redirect to menu page and show error message ONLY when the SSR is finished
        history.push('/menu');
        setServerErrorFunc(true);
      } else if (basketUuidFromUrlParams) {
        const storedCheckoutData = getValueFromLocalStorage(FOODIT_LOCALSTORAGE.CHECKOUT_DATA);
        if (storedCheckoutData) {
          setCheckoutDataFunc(storedCheckoutData);
        }

        const basketUuidFromUrl = basketUuidFromUrlParams[0];
        getBasketFunc(basketUuidFromUrl, restaurant.code)
          .then(() => {
            persistValueToLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID, basketUuidFromUrl);
          })
          .catch((error) => { // If no basket is found, redirect to menu page and show error message
            if (error.response.status === 404) {
              history.push('/menu');
              setServerErrorFunc(true);
            }
          });
      }
    } else if (path.indexOf('/reorder') === 0) { // Reorder
      const orderId = path.replace(/\/reorder\/\b/g, '');
      reorderFunc(orderId, restaurant.code)
        .then(() => history.push('/menu'))
        .catch(() => history.push('/menu'));
    } else if (path.indexOf('/voucher') === 0) { // Voucher
      const code = path.replace(/\/voucher\/\b/g, '');
      addVoucherFunc(basket.uuid, code)
        .then(() => history.push('/menu'))
        .catch(() => history.push('/menu'));
    } else if ( // Other page case, basketUuid from localstorage
      storedBasketUuid && !basket.uuid &&
      path.indexOf('confirmation') === -1 &&
      params.get(STATUSES.STATUS) !== STATUSES.SUBMITTED
    ) {
      const storedCheckoutData = getValueFromLocalStorage(FOODIT_LOCALSTORAGE.CHECKOUT_DATA);
      if (storedCheckoutData) {
        setCheckoutDataFunc(storedCheckoutData);
      }

      getBasketFunc(storedBasketUuid, restaurant.code)
        .catch((e) => {
          if (e.response && e.response.status === 404) { // If no basket is found, delete local storage
            removeValueFromLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID);
          }
        });
    } else if ( // Confirmation page with 'submitted' status case, basketuuid deleted from local stora and basket deleted from state
      path.indexOf('confirmation') > -1 &&
      params.get(STATUSES.STATUS) === STATUSES.SUBMITTED
    ) {
      deleteBasketFunc();
    }
  }

  componentDidMount() {
    GoogleAnalyticsHelper.logPageView();
    const runtime = require('serviceworker-webpack-plugin/lib/runtime');
    if (
      'serviceWorker' in navigator &&
      (window.location.protocol === 'https:' || window.location.hostname === 'localhost')
    ) {
      runtime.register();
    }
    const {location} = this.props;
    const params = new URLSearchParams(location.search);
    const tableId = params.get(URL_QUERY_PARAMS.TABLE_ID);
    this.navUrls = getSiteUrls(tableId);
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      GoogleAnalyticsHelper.logPageView();
    }
  }

  showGalleryPage(menu) {
    let show = false;
    for (const meal in menu.meals) {
      if (menu.meals[meal].primaryImageUrl) {
        show = true;
        break;
      }
    }
    return show;
  }

  render() {
    const {location, language, template, changeLanguageFunc, basket, serverOffline} = this.props;
    const preferredLanguage = getValueFromLocalStorage(FOODIT_LOCALSTORAGE.LANGUAGE);
    const restaurant = this.context.providers.restaurant.getMeta();
    const menu = this.context.providers.restaurant.getMenu();
    const offlineMenus = this.context.providers.restaurant.getOfflineMenus();
    const showGalleryPage = this.showGalleryPage(menu) && !!(template.navigationMenu && template.navigationMenu.showGalleryLink);

    const path = location.pathname;
    const headTags = seoHelper(restaurant, path);
    const isMenuPage = path.indexOf('/menu') !== -1;
    const isOfflineMenuPage = path.indexOf('/offlineMenu') !== -1;
    const isCheckoutPage = path.indexOf('/order') !== -1 && path.indexOf('/confirmation') === -1;
    const isConfirmation = path.indexOf('/confirmation') !== -1;
    const isReorder = path.indexOf('/reorder') !== -1;
    const isVoucher = path.indexOf('/voucher') !== -1;
    const isExpandedFooter = isMenuPage && basket.items.length > 0 || isCheckoutPage;

    if (preferredLanguage && preferredLanguage !== language) {
      changeLanguageFunc(preferredLanguage);
    }
    const selectedLanguage = preferredLanguage || language;
    const emptyTheme = {};
    const isTradeShow = templates[template.name].preferences.template === 'tradeShow' && path === '/';
    const templateConfig = {
      language,
      template,
      extraInfo: isMenuPage || isCheckoutPage || isConfirmation || isReorder || isOfflineMenuPage || isVoucher,
      isTradeShow,
      showGalleryPage,
      isExpandedFooter,
      offlineMenus,
      mobileOS: this.mobileOS
    };

    const renderAppThemeContent = restaurant &&
      ThemeAllocator(template.name, restaurant, templateConfig, this.navUrls, serverOffline, location);
    const renderAppContent = restaurant.modes.offlineMode ? <UnderMaintenance /> : renderAppThemeContent;
    return (
      <div>
        <Helmet {...headTags} />
        {
          language && template &&
          <ThemeProvider theme={templates[template.name] || emptyTheme}>
            <IntlProvider
              locale={selectedLanguage}
              messages={translations[selectedLanguage]}
            >
              {renderAppContent}
            </IntlProvider>
          </ThemeProvider>
        }
      </div>
    );
  }
}

const mapStateToProps = state => ({
    language: state.language,
    template: state.template,
    basket: state.basket,
    serverOffline: state.serverOffline
  });

const mapDispatchToProps = dispatch => {
    return {
      changeLanguageFunc: bindActionCreators(changeLanguage, dispatch),
      getBasketFunc: bindActionCreators(getBasket, dispatch),
      setCheckoutDataFunc: bindActionCreators(setCheckoutData, dispatch),
      deleteBasketFunc: bindActionCreators(deleteBasket, dispatch),
      setServerErrorFunc: bindActionCreators(showServerErrorAlert, dispatch),
      reorderFunc: bindActionCreators(reorder, dispatch),
      addVoucherFunc: bindActionCreators(addVoucher, dispatch),
      setOrderSourceFunc: bindActionCreators(setOrderSource, dispatch)
    };
  };

App.propTypes = {
  children: PropTypes.node,
  template: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  changeLanguageFunc: PropTypes.func.isRequired,
  getBasketFunc: PropTypes.func.isRequired,
  basket: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  setCheckoutDataFunc: PropTypes.func.isRequired,
  deleteBasketFunc: PropTypes.func.isRequired,
  setServerErrorFunc: PropTypes.func.isRequired,
  reorderFunc: PropTypes.func.isRequired,
  addVoucherFunc: PropTypes.func.isRequired,
  setOrderSourceFunc: PropTypes.func.isRequired,
  serverOffline: PropTypes.bool
};

App.contextTypes = {
  providers: PropTypes.object
};

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(App));



// WEBPACK FOOTER //
// ./containers/App/App.js